import { useRef, useCallback, useEffect } from "react";

export interface KalimbaSettings {
  volume: number;
  decay: number;
  buzz: number;
  reverb: number;
  tone: number;
}

export function useKalimbaSynth() {
  const ctxRef = useRef<AudioContext | null>(null);
  const masterGainRef = useRef<GainNode | null>(null);
  const compressorRef = useRef<DynamicsCompressorNode | null>(null);
  const noiseBufferRef = useRef<AudioBuffer | null>(null);
  const reverbNodeRef = useRef<ConvolverNode | null>(null);
  const reverbGainRef = useRef<GainNode | null>(null);
  const dryGainRef = useRef<GainNode | null>(null);

  const settingsRef = useRef<KalimbaSettings>({
    volume: 0.8,
    decay: 0.7,
    buzz: 0.05,
    reverb: 0.3,
    tone: 0.7,
  });

  const init = useCallback(() => {
    if (ctxRef.current) {
      // Resume if suspended (e.g., after phone lock/unlock)
      if (ctxRef.current.state === "suspended") {
        ctxRef.current.resume();
      }
      return ctxRef.current;
    }

    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    ctxRef.current = ctx;

    // Try to resume immediately
    if (ctx.state === "suspended") {
      ctx.resume();
    }

    // Create Noise Buffer once
    if (!noiseBufferRef.current) {
      const bufferSize = ctx.sampleRate * 0.05; // 50ms
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
      noiseBufferRef.current = buffer;
    }

    // Create reverb impulse response
    if (!reverbNodeRef.current) {
      const reverbTime = 1.5;
      const sampleRate = ctx.sampleRate;
      const length = sampleRate * reverbTime;
      const impulse = ctx.createBuffer(2, length, sampleRate);

      for (let channel = 0; channel < 2; channel++) {
        const channelData = impulse.getChannelData(channel);
        for (let i = 0; i < length; i++) {
          // Exponential decay with some randomness
          channelData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, 2.5);
        }
      }

      reverbNodeRef.current = ctx.createConvolver();
      reverbNodeRef.current.buffer = impulse;
    }

    // Master Chain: MasterGain -> Dry/Wet -> Compressor -> Destination
    masterGainRef.current = ctx.createGain();
    masterGainRef.current.gain.value = settingsRef.current.volume;

    // Dry/Wet mix for reverb
    dryGainRef.current = ctx.createGain();
    dryGainRef.current.gain.value = 1 - settingsRef.current.reverb * 0.5;

    reverbGainRef.current = ctx.createGain();
    reverbGainRef.current.gain.value = settingsRef.current.reverb;

    compressorRef.current = ctx.createDynamicsCompressor();
    compressorRef.current.threshold.value = -24;
    compressorRef.current.knee.value = 10;
    compressorRef.current.ratio.value = 20;
    compressorRef.current.attack.value = 0.001;
    compressorRef.current.release.value = 0.1;

    // Connect: master -> dry -> compressor
    //          master -> reverb -> reverbGain -> compressor
    masterGainRef.current.connect(dryGainRef.current);
    dryGainRef.current.connect(compressorRef.current);

    masterGainRef.current.connect(reverbNodeRef.current);
    reverbNodeRef.current.connect(reverbGainRef.current);
    reverbGainRef.current.connect(compressorRef.current);

    compressorRef.current.connect(ctx.destination);

    return ctx;
  }, []);

  const updateSettings = useCallback((settings: Partial<KalimbaSettings>) => {
    settingsRef.current = { ...settingsRef.current, ...settings };
    if (masterGainRef.current && settings.volume !== undefined) {
      masterGainRef.current.gain.value = settings.volume;
    }
    if (reverbGainRef.current && settings.reverb !== undefined) {
      reverbGainRef.current.gain.value = settings.reverb;
      if (dryGainRef.current) {
        dryGainRef.current.gain.value = 1 - settings.reverb * 0.5;
      }
    }
  }, []);

  const playTine = useCallback(
    (freq: number, panValue = 0, velocity = 127) => {
      const ctx = init();
      if (ctx.state === "suspended") ctx.resume();

      const now = ctx.currentTime;
      const { decay, buzz, tone } = settingsRef.current;

      const pitchScale = Math.max(0.2, 1 - (freq - 261) / 5000);
      const fundamentalDecay = (1.0 + decay * 7.0) * pitchScale;

      // Voice Master (Reduced gain to prevent clipping with multiple notes)
      const velocityScale = velocity / 127;
      const master = ctx.createGain();
      master.gain.value = 0.25 * velocityScale;

      const panner = ctx.createStereoPanner();
      panner.pan.value = panValue;

      // Tone filter (lowpass)
      const toneFilter = ctx.createBiquadFilter();
      toneFilter.type = "lowpass";
      toneFilter.frequency.value = 800 + tone * 4000; // 800Hz - 4800Hz
      toneFilter.Q.value = 0.5;

      master.connect(toneFilter);
      toneFilter.connect(panner);
      panner.connect(masterGainRef.current!);

      // --- 1. TONAL COMPONENT ---

      // Fundamental
      const fundOsc = ctx.createOscillator();
      fundOsc.type = "sine";
      fundOsc.frequency.value = freq;

      const fundGain = ctx.createGain();
      fundGain.gain.setValueAtTime(0, now);
      fundGain.gain.linearRampToValueAtTime(1.0, now + 0.005);
      fundGain.gain.exponentialRampToValueAtTime(0.0001, now + fundamentalDecay);

      fundOsc.connect(fundGain);
      fundGain.connect(master);

      // Overtone (5th harmonic - cleaner than 6.1x)
      const overtoneOsc = ctx.createOscillator();
      overtoneOsc.type = "sine";
      overtoneOsc.frequency.value = freq * 5;

      const overtoneGain = ctx.createGain();
      overtoneGain.gain.setValueAtTime(0, now);
      overtoneGain.gain.linearRampToValueAtTime(0.15, now + 0.002);
      overtoneGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);

      overtoneOsc.connect(overtoneGain);
      overtoneGain.connect(master);

      // Transient (17x)
      const transOsc = ctx.createOscillator();
      transOsc.type = "sine";
      transOsc.frequency.value = freq * 17.0;

      const transGain = ctx.createGain();
      transGain.gain.setValueAtTime(0, now);
      transGain.gain.linearRampToValueAtTime(0.05, now + 0.001);
      transGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.04);

      transOsc.connect(transGain);
      transGain.connect(master);

      // --- 2. NOISE & BODY ---

      // Nail Click
      const clickSrc = ctx.createBufferSource();
      if (noiseBufferRef.current) {
        clickSrc.buffer = noiseBufferRef.current;
      }

      const clickFilter = ctx.createBiquadFilter();
      clickFilter.type = "bandpass";
      clickFilter.frequency.value = 2500;
      clickFilter.Q.value = 1.0;

      const clickGain = ctx.createGain();
      clickGain.gain.setValueAtTime(0.2, now);
      clickGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.015);

      clickSrc.connect(clickFilter);
      clickFilter.connect(clickGain);
      clickGain.connect(master);

      // Wood Thump (relative to note - sub-octave)
      const thumpFreq = Math.max(60, freq * 0.5); // Sub-octave, min 60Hz
      const thumpOsc = ctx.createOscillator();
      thumpOsc.type = "sine";
      thumpOsc.frequency.setValueAtTime(thumpFreq, now);
      thumpOsc.frequency.exponentialRampToValueAtTime(thumpFreq * 0.7, now + 0.08);

      const thumpGain = ctx.createGain();
      thumpGain.gain.setValueAtTime(0.3, now);
      thumpGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);

      const thumpFilter = ctx.createBiquadFilter();
      thumpFilter.type = "lowpass";
      thumpFilter.frequency.value = 250;

      thumpOsc.connect(thumpFilter);
      thumpFilter.connect(thumpGain);
      thumpGain.connect(master);

      // --- 3. BUZZ (metal rattle - like bottle caps on tines) ---
      const buzzDuration = 0.15 + decay * 0.2;

      if (buzz > 0.01) {
        // Sawtooth oscillator with ring modulation for metallic buzz
        const buzzOsc = ctx.createOscillator();
        buzzOsc.type = "sawtooth";
        buzzOsc.frequency.value = freq * 2.02; // Slightly detuned = beating

        const buzzOsc2 = ctx.createOscillator();
        buzzOsc2.type = "square";
        buzzOsc2.frequency.value = freq * 3.01; // Another detuned harmonic

        const buzzGain = ctx.createGain();
        buzzGain.gain.setValueAtTime(0, now);
        buzzGain.gain.linearRampToValueAtTime(buzz * 0.12, now + 0.005);
        buzzGain.gain.exponentialRampToValueAtTime(0.0001, now + buzzDuration);

        buzzOsc.connect(buzzGain);
        buzzOsc2.connect(buzzGain);
        buzzGain.connect(master);

        buzzOsc.start(now);
        buzzOsc2.start(now);
        buzzOsc.stop(now + buzzDuration + 0.05);
        buzzOsc2.stop(now + buzzDuration + 0.05);
      }

      // Start
      fundOsc.start(now);
      overtoneOsc.start(now);
      transOsc.start(now);
      clickSrc.start(now);
      thumpOsc.start(now);

      // Stop & Cleanup
      const maxDuration = fundamentalDecay + 0.5;

      fundOsc.stop(now + maxDuration);
      overtoneOsc.stop(now + maxDuration);
      transOsc.stop(now + maxDuration);
      // clickSrc stops naturally
      thumpOsc.stop(now + 0.2);

      setTimeout(() => {
        master.disconnect();
        fundGain.disconnect();
        overtoneGain.disconnect();
        transGain.disconnect();
        clickGain.disconnect();
        thumpGain.disconnect();
        toneFilter.disconnect();
      }, maxDuration * 1000);
    },
    [init]
  );

  // Resume audio context when page becomes visible (after phone lock/unlock)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && ctxRef.current) {
        // Try to resume immediately
        ctxRef.current.resume();
        // Retry after a short delay (mobile sometimes needs this)
        setTimeout(() => {
          if (ctxRef.current?.state === "suspended") {
            ctxRef.current.resume();
          }
        }, 100);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (ctxRef.current) {
        ctxRef.current.close();
        ctxRef.current = null;
      }
    };
  }, []);

  return { playTine, updateSettings, init };
}

export function getDefaultSettings(): KalimbaSettings {
  return { volume: 0.8, decay: 0.7, buzz: 0.05, reverb: 0.3, tone: 0.7 };
}
